import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { fetchOrders, getRestaurantProfile } from '../utils/api';
import AuthContext from '../contexts/AuthContext';
import { FaFileCsv, FaChartBar, FaListAlt, FaFileAlt, FaChevronDown, FaChevronUp, FaCalendarAlt } from 'react-icons/fa';



// Report Components
const reportTypes = [
  { id: 'summary', name: 'Sales Summary', icon: <FaChartBar /> },
  { id: 'itemSales', name: 'Item-wise Sales', icon: <FaListAlt /> },
  { id: 'fullHistory', name: 'Detailed Order History', icon: <FaFileAlt /> },
];

const Modal = ({ open, onClose, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={onClose}>
      <div className="bg-gray-800 rounded-lg shadow-lg p-6 min-w-[250px] relative" onClick={e => e.stopPropagation()}>
        {children}
        <button onClick={onClose} className="absolute top-2 right-2 text-gray-400 hover:text-white text-lg">&times;</button>
      </div>
    </div>
  );
};

export default function Reports() {
  const { restaurantId } = useContext(AuthContext);
  const [restaurant, setRestaurant] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([new Date(new Date().setDate(new Date().getDate() - 30)), new Date()]);
  const [startDate, endDate] = dateRange;
  const [activeReport, setActiveReport] = useState('summary');
  const [showFilters, setShowFilters] = useState(true);
  const [reportTypeModal, setReportTypeModal] = useState(false);
  const [dateModalOpen, setDateModalOpen] = useState(false);

  useEffect(() => {
    if (restaurantId) {
      setLoading(true);
      const isValidDate = d => d instanceof Date && !isNaN(d);
      if (isValidDate(startDate) && isValidDate(endDate)) {
        Promise.all([
          fetchOrders(restaurantId, startDate.toISOString(), endDate.toISOString()),
          getRestaurantProfile(restaurantId)
        ]).then(([orderData, profileData]) => {
          setOrders(orderData || []);
          setRestaurant(profileData);
          setLoading(false);
        }).catch(err => {
          console.error("Error fetching report data:", err);
          setLoading(false);
        });
      } else {
        setOrders([]);
        setLoading(false);
      }
    }
  }, [restaurantId, dateRange, activeReport]);

  const handleExport = () => {
    let csvContent = "data:text/csv;charset=utf-8,";
    let headers = [];
    let rows = [];
    let filename = `${activeReport}-report.csv`;

    switch (activeReport) {
      case 'summary':
        headers = ["Metric", "Value"];
        const summaryData = getSummaryData(orders);
        rows = summaryData.map(stat => [stat.label, stat.value]);
        break;
      case 'itemSales':
        headers = ["Item Name", "Quantity Sold", "Total Revenue"];
        rows = getItemSalesData(orders).map(item => [item.name, item.qty, item.total.toFixed(2)]);
        break;
      case 'fullHistory':
        // include captain column
        headers = ["Order ID", "Date", "Table", "Captain", "Items Count", "Total"];
        rows = orders.map(order => [
          order.id.slice(-5),
          new Date(order.createdAt._seconds * 1000).toLocaleDateString(),
          order.tableName || `Table ${order.tableId}`,
          order.captainName || "Unassigned",
          order.items.length,
          (order.total * 1.05).toFixed(2)
        ]);
        break;
      default:
        return;
    }

    csvContent += [headers.join(","), ...rows.map(row => row.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-full bg-gradient-to-br from-rose-50 to-amber-50 p-3 sm:p-4 md:p-8 rounded-xl sm:rounded-2xl">
      {/* Page Header and Actions */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4 sm:mb-6 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-black">Reports</h1>
          <p className="text-black-400 text-sm sm:text-base">Generate and view detailed reports for your restaurant.</p>
        </div>
        <div className="flex gap-2 sm:gap-3">
          <button 
            className="bg-gray-800 hover:bg-gray-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-2 transition-all text-sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? <FaChevronUp /> : <FaChevronDown />}
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button onClick={handleExport} className="bg-gray-800 hover:bg-gray-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-2 transition-all text-sm">
            <FaFileCsv /> CSV
          </button>
        </div>
      </div>

      {/* Filters Panel - Horizontal */}
      <AnimatePresence>
        {showFilters && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-gray-800 p-3 sm:p-4 rounded-xl border border-gray-700 mb-4 sm:mb-6 overflow-hidden"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Report Type Modal Button */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wider">Report Type</label>
                <button
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm bg-gray-700 text-white hover:bg-gray-600 transition-all border border-gray-600"
                  onClick={() => setReportTypeModal(true)}
                  type="button"
                >
                  <span className="flex items-center gap-2">
                    {reportTypes.find(t => t.id === activeReport)?.icon}
                    {reportTypes.find(t => t.id === activeReport)?.name}
                  </span>
                  <FaChevronDown className="ml-2" />
                </button>
                <Modal open={reportTypeModal} onClose={() => setReportTypeModal(false)}>
                  <div className="mb-4 text-lg font-semibold text-white">Select Report Type</div>
                  <div className="space-y-2">
                    {reportTypes.map(type => (
                      <button
                        key={type.id}
                        onClick={() => {
                          setActiveReport(type.id);
                          setReportTypeModal(false);
                        }}
                        className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-all text-left ${activeReport === type.id ? 'bg-purple-600 text-white' : 'text-gray-200 hover:bg-gray-600'}`}
                      >
                        {type.icon}
                        {type.name}
                      </button>
                    ))}
                  </div>
                </Modal>
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wider">Date Range</label>
                <div className="relative flex gap-2 items-center">
                  <button
                    type="button"
                    onClick={() => setDateModalOpen(true)}
                    className="flex-1 flex items-center bg-gray-700 border border-gray-600 rounded-lg py-2 pl-3 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm transition-all"
                  >
                    <FaCalendarAlt className="mr-2 text-purple-400" />
                    <span className="truncate">
                      {startDate && endDate
                        ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                        : 'Select date range'}
                    </span>
                  </button>
                  {/* Today Button */}
                  <button
                    type="button"
                    className="ml-2 px-2 sm:px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs sm:text-sm font-semibold transition-all"
                    onClick={() => {
                      const now = new Date();
                      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
                      const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                      setDateRange([todayStart, todayEnd]);
                    }}
                  >
                    Today
                  </button>
                </div>
                <Modal open={dateModalOpen} onClose={() => setDateModalOpen(false)}>
                  <div className="mb-2 text-lg font-semibold text-white">Select Date Range</div>
                  <div className="mb-4 text-sm text-purple-300">
                    {(!startDate || (startDate && endDate)) ? 'Select start date' : 'Select end date'}
                  </div>
                  <DatePicker
                    inline
                    selectsRange
                    startDate={startDate}
                    endDate={endDate}
                    onChange={update => {
                      setDateRange(update);
                      // Close modal if both dates are selected
                      if (update[0] && update[1]) setTimeout(() => setDateModalOpen(false), 300);
                    }}
                    monthsShown={2}
                    calendarClassName="bg-gray-800 text-white rounded-lg shadow-lg border border-gray-700"
                  />
                </Modal>
              </div>

              {/* Spacer for future filters */}
              <div></div>

              {/* Generate Button */}
              <div className="flex items-end">
                <button
                  onClick={() => {}}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium py-2 px-3 sm:px-4 rounded-lg flex items-center justify-center gap-2 transition-all text-sm"
                >
                  <FaFileCsv />
                  Generate Report
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content (unchanged for now) */}
      <div className="flex flex-col md:flex-row gap-6 sm:gap-8">
        {/* Report Display Area */}
        <main className="w-full">
          {loading ? (
            <div className="text-center p-6 sm:p-8 text-white">Loading report data...</div>
          ) : (
            <div>
              <ReportHeader restaurant={restaurant} dateRange={dateRange} />
              {activeReport === 'summary' && <SalesSummary orders={orders} />}
              {activeReport === 'itemSales' && <ItemSalesReport orders={orders} />}
              {activeReport === 'fullHistory' && <DetailedOrderHistory orders={orders} />}
            </div>
          )}
        </main>
      </div>
    </motion.div>
  );
}

const ReportHeader = ({ restaurant, dateRange }) => (
  <div className="mb-8">
    <h2 className="text-2xl font-bold">{restaurant?.name || 'Restaurant Report'}</h2>
    <p className="text-gray-600">
      For the period: {dateRange[0] ? dateRange[0].toLocaleDateString() : 'N/A'} - {dateRange[1] ? dateRange[1].toLocaleDateString() : 'N/A'}
    </p>
  </div>
);

// Helper function to process summary data
const getSummaryData = (orders) => {
  const preTaxRevenue = orders.reduce((sum, o) => sum + o.total, 0);
  const totalRevenue = preTaxRevenue * 1.05;
  const totalOrders = orders.length;
  const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
  const totalTax = preTaxRevenue * 0.05;
  return [
    { label: 'Total Revenue', value: `₹${totalRevenue.toFixed(2)}` },
    { label: 'Total Orders', value: totalOrders },
    { label: 'Avg. Order Value', value: `₹${avgOrderValue.toFixed(2)}` },
    { label: 'Total Tax (5%)', value: `₹${totalTax.toFixed(2)}` },
  ];
};

// Individual Report Components
const SalesSummary = ({ orders }) => {
  const stats = getSummaryData(orders);
  return (
    <div>
      <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Sales Summary</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {stats.map(stat => (
          <div key={stat.label} className="bg-gray-50 p-3 sm:p-4 rounded-lg text-center shadow">
            <p className="text-xs sm:text-sm text-gray-500">{stat.label}</p>
            <p className="text-lg sm:text-2xl font-bold text-gray-800">{stat.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper function to process item sales data
const getItemSalesData = (orders) => {
  const itemSales = orders.flatMap(o => o.items).reduce((acc, item) => {
    if (!acc[item.name]) {
      acc[item.name] = { name: item.name, qty: 0, total: 0 };
    }
    acc[item.name].qty += item.qty;
    acc[item.name].total += (item.price * item.qty) * 1.05;
    return acc;
  }, {});
  return Object.values(itemSales).sort((a, b) => b.qty - a.qty);
};

const ITEM_SALES_TABLE_HEADERS = [
  { label: '#', key: 'number' },
  { label: 'Item Name', key: 'name' },
  { label: 'Quantity Sold', key: 'qty' },
  { label: 'Total Revenue', key: 'total' },
];

const ORDER_HISTORY_TABLE_HEADERS = [
  { label: '#', key: 'number' },
  { label: 'Order ID', key: 'id' },
  { label: 'Date', key: 'date' },
  { label: 'Table', key: 'table' },
  { label: 'Captain', key: 'captain' },
  { label: 'Items', key: 'items' },
  { label: 'Total', key: 'total' },
];

const ItemSalesReport = ({ orders }) => {
  const sortedItems = getItemSalesData(orders);
  const totalRevenue = sortedItems.reduce((sum, item) => sum + item.total, 0);
  return (
    <div>
      <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Item-wise Sales</h3>
      <div className="rounded-lg shadow overflow-hidden">
        <div className="max-h-96 overflow-y-auto">
          <table className="min-w-full bg-white">
            <thead className="sticky top-0 bg-gray-50 z-10">
              <tr>
                {ITEM_SALES_TABLE_HEADERS.map((header) => (
                  <th key={header.key} className="px-2 sm:px-4 py-2 text-left font-semibold text-gray-700 text-xs sm:text-sm">{header.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((item, idx) => (
                <tr key={item.name} className="border-b">
                  <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">{idx + 1}</td>
                  <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">{item.name}</td>
                  <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">{item.qty}</td>
                  <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">₹{item.total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-100 font-bold">
                <td className="px-2 sm:px-4 py-2 text-right text-xs sm:text-sm" colSpan={3}>Total</td>
                <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">₹{totalRevenue.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

const DetailedOrderHistory = ({ orders }) => {
  const total = orders.reduce((sum, order) => sum + (order.total * 1.05), 0);
  return (
    <div>
      <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Detailed Order History</h3>
      <div className="rounded-lg shadow overflow-hidden">
        <div className="max-h-96 overflow-y-auto">
          <table className="min-w-full bg-white">
            <thead className="sticky top-0 bg-gray-50 z-10">
              <tr>
                {ORDER_HISTORY_TABLE_HEADERS.map((header) => (
                  <th key={header.key} className="px-2 sm:px-4 py-2 text-left font-semibold text-gray-700 text-xs sm:text-sm">{header.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((order, idx) => (
                <tr key={order.id} className="border-b">
                  <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">{idx + 1}</td>
                  <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">#{order.id.slice(-5)}</td>
                  <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">{new Date(order.createdAt._seconds * 1000).toLocaleDateString()}</td>
                  <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">{order.tableName || `Table ${order.tableId}`}</td>
                  <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">{order.captainName || 'Unassigned'}</td>
                  <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">{order.items.length}</td>
                  <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">₹{(order.total * 1.05).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-100 font-bold">
                <td className="px-2 sm:px-4 py-2 text-right text-xs sm:text-sm" colSpan={6}>Total</td>
                <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">₹{total.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};